// src/hooks/useHomeContactInsights.ts
// Selects and prioritizes contact insights for Home display.
// Fetches a wider contact pool, fetches context for each, then filters + sorts.
// Home only consumes CRM-computed intelligence — no recomputation here.

import { useMemo } from 'react';
import { useGetContactsQuery, useGetContactContextQuery } from '../api/contactsApi';
import type { ContactContextResponse } from '../types/api';

/** Fetch a wider pool so important contacts aren't missed by arbitrary ordering */
const CONTACT_FETCH_LIMIT = 20;
/** Max contacts to fetch context for (bounded for performance) */
const MAX_CONTEXT_SLOTS = 10;
const MAX_DISPLAYED = 3;

/**
 * Fetches context for a single contact. Safe to call conditionally via skip.
 */
function useContactContext(contactId: string | undefined) {
  return useGetContactContextQuery(contactId!, {
    skip: !contactId,
  });
}

export interface HomeContactInsight {
  contactId: string;
  context: ContactContextResponse;
  /** Sort priority: lower = more important */
  priority: number;
}

/**
 * Hook for Home to consume contact insights.
 *
 * Strategy:
 * 1. Fetch first 20 contacts from workspace (wider pool)
 * 2. Sort by updatedAt DESC client-side (recently active first)
 * 3. Take top 10 for context fetching
 * 4. Filter to those with actionable signals
 * 5. Sort by priority (urgent tasks > upcoming events > stale)
 * 6. Return top 3
 */
export function useHomeContactInsights(workspaceId: string | undefined) {
  // Step 1: Fetch a wider contact pool
  const { data: contactsData, isLoading: isLoadingContacts } = useGetContactsQuery(
    { workspaceId, limit: CONTACT_FETCH_LIMIT },
    { skip: !workspaceId }
  );

  // Step 2: Sort by updatedAt DESC and take top 10 for context fetching
  const contactIds = useMemo(() => {
    const contacts = contactsData?.contacts ?? [];
    return [...contacts]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, MAX_CONTEXT_SLOTS)
      .map((c) => c.id);
  }, [contactsData]);

  // Step 3: Fetch context for each (RTK Query caches individually)
  // Fixed hook count — each skips when contactId is undefined
  const ctx0 = useContactContext(contactIds[0]);
  const ctx1 = useContactContext(contactIds[1]);
  const ctx2 = useContactContext(contactIds[2]);
  const ctx3 = useContactContext(contactIds[3]);
  const ctx4 = useContactContext(contactIds[4]);
  const ctx5 = useContactContext(contactIds[5]);
  const ctx6 = useContactContext(contactIds[6]);
  const ctx7 = useContactContext(contactIds[7]);
  const ctx8 = useContactContext(contactIds[8]);
  const ctx9 = useContactContext(contactIds[9]);

  const contextResults = [ctx0, ctx1, ctx2, ctx3, ctx4, ctx5, ctx6, ctx7, ctx8, ctx9];

  // Step 4+5: Filter and prioritize
  const insights = useMemo(() => {
    const items: HomeContactInsight[] = [];

    for (let i = 0; i < contactIds.length; i++) {
      const result = contextResults[i];
      if (!result?.data) continue;

      const ctx = result.data;
      const { signals } = ctx;

      // Only show contacts with actionable signals
      const hasUrgent = signals.urgentTasks.length > 0;
      const hasUpcoming = signals.upcomingEvents.length > 0;
      const isStale = signals.staleInteraction;

      if (!hasUrgent && !hasUpcoming && !isStale) continue;

      // Priority: urgent tasks (0) > upcoming events (1) > stale only (2)
      let priority = 3;
      if (hasUrgent) priority = 0;
      else if (hasUpcoming) priority = 1;
      else if (isStale) priority = 2;

      items.push({
        contactId: contactIds[i],
        context: ctx,
        priority,
      });
    }

    items.sort((a, b) => a.priority - b.priority);
    return items.slice(0, MAX_DISPLAYED);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contactIds,
    ctx0.data, ctx1.data, ctx2.data, ctx3.data, ctx4.data,
    ctx5.data, ctx6.data, ctx7.data, ctx8.data, ctx9.data,
  ]);

  const isLoading = isLoadingContacts || contextResults.some((r) => r.isLoading);

  return {
    insights,
    isLoading,
    isEmpty: !isLoading && insights.length === 0,
  };
}
